import { DownloadArrow } from '#assets/ReactSvg.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import type { HostNoun } from '#dn-types/host.ts';
import type { EnrollmentState } from '../types.ts';
import { TokenStatus } from './token-instructions/TokenStatus.tsx';
import styles from './MacOSInstructions.module.css';

type Props = {
  hostNoun: HostNoun;
  enrollmentState: EnrollmentState;
  downloadURL: string;
};

export function MacOSInstructions({ hostNoun, enrollmentState, downloadURL }: Props) {
  const { token, isExpired, enrollSuccessful, handleDone } = enrollmentState;

  return (
    <div className={styles.MacOSInstructions}>
      <div className="mb-40">
        <h2 className="h3">2. Download and install DNClient Desktop</h2>
        <p className="p-md">
          Want to use dnclient on the command line?{' '}
          <Link
            className="a"
            to="https://www.defined.net/docs/get-started/dnclient-server/install/"
            reloadDocument={true}
            target="_blank"
            rel="help"
          >
            View docs
          </Link>
        </p>
        <LinkButton className="flex gap-8 mb-16" variant="secondary" size="small" download={true} to={downloadURL}>
          <DownloadArrow className={styles.MacOSInstructions_downloadButtonIcon} /> Download DNClient Desktop
        </LinkButton>
        <ol>
          <li className="p-md">
            Navigate to the folder where you downloaded the DNClient-Desktop.dmg file, and open it.
          </li>
          <li className="p-md">Drag the icon for DNClient Desktop into the shortcut for Applications.</li>
          <li className="p-md">
            After that, youʼll be able to open DNClient Desktop like any other app on your computer (from spotlight,
            launchpad, the Applications folder in finder, etc).
          </li>
        </ol>
        <p className="p-md">
          The first time you launch the app, you will be guided through the process of allowing the program to run in
          the background at startup. This is necessary in order for DNClient Desktop to function correctly—to be able to
          spawn dnclient connections.
        </p>
      </div>
      <div>
        <h2 className="h3">3. Enroll DNClient Desktop</h2>
        <p className="p-md">To enroll into a network:</p>
        <ol>
          <li className="p-md">
            Click the DNClient Desktop menu icon at the top of your screen, and select{' '}
            <kbd>
              <samp>“Enroll device”</samp>
            </kbd>{' '}
            from the menu.
          </li>
          <li className="p-md">
            Paste the enrollment code into the window that appears, and click the button labelled{' '}
            <kbd>
              <samp>“Enroll device.”</samp>
            </kbd>
          </li>
        </ol>
        {token ?
          <Snippet className="width-fit-content" disabled={isExpired} breakWord={true}>
            {token}
          </Snippet>
        : <p className="p-md mb-24">Getting enrollment code…</p>}
        <TokenStatus hostNoun={hostNoun} enrollSuccessful={enrollSuccessful} handleDone={handleDone} />
      </div>
    </div>
  );
}
